import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { isModelStale, processCoreModelResponse } from "@/store/-utils";
import ProfilesApi from "@/store/profiles/-api";

@Module({
  name: "profiles",
  stateFactory: true,
  namespaced: true,
})
export default class ProfilesStore extends VuexModule {
  profiles: ProfileMap = {};

  get getIdByDisplayName() {
    return (displayName: string) => {
      const profile = Object.values(this.profiles).find(
        (item) => item && item.displayName?.toLowerCase() === displayName?.toLowerCase(),
      );

      return profile?.id;
    };
  }

  get getDisplayNameById() {
    return (profileId: string | undefined) => {
      if (profileId === undefined) {
        return;
      }
      return this.profiles[profileId]?.displayName;
    };
  }

  get hasProfileBeenFetchedByName() {
    return (displayName: string) => {
      const id = this.getIdByDisplayName(displayName);
      return !!id;
    };
  }

  get getProfileById() {
    return (id: string | null | undefined) => {
      if (!id) {
        return;
      }

      return this.profiles[id];
    };
  }

  get getProfileByDisplayName() {
    return (displayName: string | null | undefined) => {
      if (!displayName) {
        return;
      }
      const id = this.getIdByDisplayName(displayName);
      return this.getProfileById(id);
    };
  }

  @Action({ rawError: true })
  async fetchProfilesByIds({ ids }: { ids: string[] }) {
    if (ids.length === 0) {
      return;
    }
    const api = new ProfilesApi(this.store.$axios);
    const response = await api.get({ ids });

    await processCoreModelResponse(this.store, response.data);
  }

  /**
   * Normally calling 1 mutation for every 1 model would be discouraged, however, the underlying
   * API call has a special debounce and argument aggregation decorator on it. Because of this,
   * multiple rapid calls to the API will be aggregated into a single call, resulting with only
   * the last call returning results, and the earlier ones having a rejected/cancelled promise.
   */
  @Action({ rawError: true })
  async fetchProfileById(id: string) {
    // Skip refreshing recent models
    if (!isModelStale(this.profiles[id])) {
      return;
    }

    try {
      const api = new ProfilesApi(this.store.$axios);
      const response = await api.getOne(id);

      await processCoreModelResponse(this.store, response.data);
    } catch (error) {
      // null is returned from the debounce decorator. All other errors should be considered action failures.
      if (error === null) {
        return undefined;
      }
    }
  }

  @Action({ rawError: true })
  async fetchProfilesByDisplayNames({ displayNames }: { displayNames: string[] }) {
    if (displayNames.length === 0) {
      return;
    }
    const api = new ProfilesApi(this.store.$axios);
    const response = await api.get({ displayNames });

    await processCoreModelResponse(this.store, response.data);
  }

  @Action({ rawError: true })
  async fetchProfileByDisplayName(displayName: string) {
    // Skip refreshing recent models
    const id = this.getIdByDisplayName(displayName);
    if (id !== undefined && !isModelStale(this.profiles[id])) {
      return;
    }

    const api = new ProfilesApi(this.store.$axios);
    const response = await api.get({ displayNames: [displayName] });

    await processCoreModelResponse(this.store, response.data);
  }

  @Mutation
  setProfiles({ profiles }: { profiles: ProfileMap }) {
    Vue.set(this, "profiles", Object.assign({}, this.profiles, profiles));
  }
}

export const getProfilesStore = (store: Store<unknown>) => {
  const profilesStore = getModule(ProfilesStore, store);

  return profilesStore;
};
